/* eslint-disable react/no-array-index-key */
import { useTranslation } from 'react-i18next'

import {
  type UpdateMovePayload,
  useCurrentMove,
  useUpdateMoveAction,
} from '../../../modules/moves'
import { useNavigate } from '../../../modules/navigation'
import { type EditableSectionProps } from '../../EditableSections/editableSections.type'
import AddressesForm from '../../forms/editmove/AddressesForm'
import Route from '../../../app/Route.enum'
import {
  errorNotification,
  successNotification,
  warningNotification,
} from '../../ToastNotifications'
import { useErrorFormatter } from '../../errors/useErrorFormatter'
import AddressRow, { AddressRowType } from './AddressRow'
import { mergeClassName } from '../../../utils/mergeClassName'

const AddressesSection: React.FC<EditableSectionProps> = ({
  isEditing,
  onClose,
  wide,
}) => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const hasStops = (move.stops?.length ?? 0) > 0
  const [updateMove] = useUpdateMoveAction()
  const { navigate } = useNavigate()
  const formatError = useErrorFormatter()

  const onSubmit = async (data: UpdateMovePayload) => {
    try {
      const hasSelection = !!move.selectedBranch
      const updatedMove = await updateMove(data)
      onClose()
      navigate(Route.Move)
      successNotification((
        <>
          { t('components.move.addresses.success') }
          <div className="font-bold">
            { t('components.move.quoteRecalculated') }
          </div>
        </>
      ))

      if (hasSelection && !updatedMove.selectedBranch) {
        warningNotification(t('components.move.unselectedQuote'))
      }
    } catch (error) {
      errorNotification(formatError(error))
    }
  }

  if (isEditing) {
    return (
      <AddressesForm
        onSubmit={onSubmit}
        onCancel={onClose}
        wide={wide && !hasStops}
      />
    )
  }

  return (
    <div className={mergeClassName(
      'my-2 flex flex-col gap-4 lg:my-4 lg:flex-row lg:gap-y-6',
      hasStops && 'lg:flex-col',
    )}
    >
      <AddressRow type={AddressRowType.Origin} data={move.origin} />
      { move.stops?.map((stop, index) => (
        <AddressRow
          key={`${index}:${stop.description}`}
          type={AddressRowType.Stop}
          data={stop}
        />
      )) }

      <AddressRow type={AddressRowType.Destination} data={move.destination} />
    </div>
  )
}

export default AddressesSection
